import type { Grid } from "../core/Grid.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Vec2 } from "../core/types.ts";
import { CellType } from "../core/types.ts";
import { CELL_SIZE } from "../core/config.ts";
import {
  type SpawnCell,
  type SpawnLayout,
  scanSpawnLayout,
} from "../core/SpawnModel.ts";

/** BL-147: a hidden-spawn detection entry — the shared {@link SpawnCell} plus
 *  its detected flag. */
export interface HiddenSpawn extends SpawnCell {
  detected: boolean;
}

/**
 * SpawnDetectionSystem — Signal towers reveal hidden spawn points.
 * Each frame, checks whether any HIDDEN_SPAWN cell falls within a
 * Signal tower's range. On detection, transitions cell to DORMANT_SPAWN
 * and emits an event for UI feedback.
 */
export class SpawnDetectionSystem {
  private hiddenSpawns: HiddenSpawn[] = [];
  private grid: Grid;
  private bus: EventBus;

  constructor(grid: Grid, bus: EventBus) {
    this.grid = grid;
    this.bus = bus;
  }

  /** Register hidden spawns from the run layout (or grid fallback). BL-147:
   *  accepts the ONE `SpawnLayout` (reads `.hidden`); the grid-rescan fallback
   *  is the shared `scanSpawnLayout`. */
  init(layout?: SpawnLayout): void {
    const resolved = layout ?? scanSpawnLayout(this.grid);
    this.hiddenSpawns = [];
    for (const pos of resolved.hidden) {
      this.hiddenSpawns.push({ col: pos.col, row: pos.row, detected: false });
    }
  }

  /** Reset for a new run — restore detected spawns back to HIDDEN_SPAWN */
  reset(): void {
    for (const s of this.hiddenSpawns) {
      if (s.detected) {
        this.grid.setCell(s.col, s.row, CellType.HIDDEN_SPAWN);
      }
      s.detected = false;
    }
  }

  /** Check all signal towers against hidden spawns (call each frame during BUILD) */
  update(towers: Tower[], rangeMultiplier: number): void {
    for (const spawn of this.hiddenSpawns) {
      if (spawn.detected) continue;
      const spawnX = spawn.col * CELL_SIZE + CELL_SIZE / 2;
      const spawnY = spawn.row * CELL_SIZE + CELL_SIZE / 2;

      for (const tower of towers) {
        if (!tower.detectStealth) continue;
        const effectiveRange = tower.range * rangeMultiplier;
        const rangeSq = effectiveRange * effectiveRange;
        const dx = tower.x - spawnX;
        const dy = tower.y - spawnY;
        if (dx * dx + dy * dy <= rangeSq) {
          this.reveal(spawn);
          break;
        }
      }
    }
  }

  /** Reveal a hidden spawn — transition to DORMANT_SPAWN + emit event */
  private reveal(spawn: HiddenSpawn): void {
    spawn.detected = true;
    if (
      this.grid.getCell(spawn.col, spawn.row)?.type === CellType.HIDDEN_SPAWN
    ) {
      this.grid.setCell(spawn.col, spawn.row, CellType.DORMANT_SPAWN);
    }
    this.bus.emit("spawn.detected", { col: spawn.col, row: spawn.row });
  }

  /**
   * Force-reveal a specific hidden spawn (fallback when a wave is about to
   * use it without Signal tower detection).
   */
  forceReveal(col: number, row: number): boolean {
    const spawn = this.hiddenSpawns.find(
      (s) => s.col === col && s.row === row && !s.detected,
    );
    if (!spawn) return false;
    spawn.detected = true;
    // Q-023: avoid clobbering a spawn already activated by BreachSystem.
    if (this.grid.getCell(col, row)?.type === CellType.HIDDEN_SPAWN) {
      this.grid.setCell(col, row, CellType.DORMANT_SPAWN);
    }
    this.bus.emit("spawn.forceRevealed", { col, row });
    return true;
  }

  /** Mark a spawn as detected without emitting events (used by signal overload) */
  markDetected(col: number, row: number): void {
    const spawn = this.hiddenSpawns.find(
      (s) => s.col === col && s.row === row && !s.detected,
    );
    if (spawn) spawn.detected = true;
  }

  /** Get all undetected hidden spawn positions */
  getHiddenPositions(): Vec2[] {
    return this.hiddenSpawns
      .filter((s) => !s.detected)
      .map((s) => ({ x: s.col, y: s.row }));
  }

  /** Check if a specific cell is still hidden */
  isHidden(col: number, row: number): boolean {
    return this.hiddenSpawns.some(
      (s) => s.col === col && s.row === row && !s.detected,
    );
  }
}
